import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, EmptyState, StatusBadge, ConfirmModal } from '../components/common';
import { UserModal, ImportCSVModal } from '../components/modals';

/**
 * Users Management page - Admin CRUD for users
 */
const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [sortBy, setSortBy] = useState('firstName');
  const [confirmState, setConfirmState] = useState(null);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aVal = (a[sortBy] || '').toLowerCase();
      const bVal = (b[sortBy] || '').toLowerCase();
      return aVal.localeCompare(bVal);
    });
  }, [users, sortBy]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setUsers(await api.get('/users'));
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (userId) => {
    setConfirmState({
      message: 'Are you sure you want to delete this user?',
      confirmLabel: 'Delete User',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await api.delete(`/users/${userId}`);
          loadUsers();
        } catch (error) {
          console.error('Failed to delete user:', error);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Users Management</h1>
        <div className="flex gap-1">
          <Button variant="secondary" onClick={() => setShowImportModal(true)}>
            Import CSV
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              setEditUser(null);
              setShowModal(true);
            }}
          >
            Add User
          </Button>
        </div>
      </div>

      {users.length === 0 ? (
        <EmptyState
          icon="👤"
          title="No users"
          message="Add your first user to get started."
        />
      ) : (
        <>
        <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Sort by:</span>
          <Button
            variant={sortBy === 'firstName' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setSortBy('firstName')}
          >
            First Name
          </Button>
          <Button
            variant={sortBy === 'lastName' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setSortBy('lastName')}
          >
            Last Name
          </Button>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(user => (
              <tr key={user.id}>
                <td>{user.firstName} {user.lastName}</td>
                <td>{user.email}</td>
                <td>
                  {user.isAdmin ? (
                    <span className="badge badge-admin">Admin</span>
                  ) : user.isManager ? (
                    <span className="badge badge-manager">Manager</span>
                  ) : (
                    <span className="badge badge-status">Member</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => {
                      setEditUser(user);
                      setShowModal(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    style={{ marginLeft: '0.25rem', color: 'var(--color-danger)' }}
                    onClick={() => handleDelete(user.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}

      {/* Modals */}
      {showModal && (
        <UserModal
          user={editUser}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            loadUsers();
          }}
        />
      )}

      {showImportModal && (
        <ImportCSVModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            loadUsers();
          }}
        />
      )}

      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

UsersManagement.propTypes = {};

export default UsersManagement;
